import { Component } from '@theme/component';
import { sectionRenderer, sectionIdFor } from '@theme/section-renderer';
import { debounce, announce, tCount } from '@theme/utilities';

/**
 * Filtering, sorting and pagination for a collection or search results.
 *
 * The controls are a real `<form method="get">` around Shopify's own filter
 * inputs, so with JavaScript unavailable the page reloads and filters
 * correctly. This upgrades that to a section re-render, which is the whole
 * point of using the storefront filter API rather than a bespoke one: the same
 * query string drives both paths.
 *
 * @extends {Component}
 */
class FacetsComponent extends Component {
  requiredRefs = ['form'];

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#onPopState);
  }

  /** A checkbox, a radio, or the sort `<select>` changed. */
  changed = () => this.#apply(this.#urlFromForm());

  /** Price range fields, which a shopper types into. */
  typed = debounce(() => this.#apply(this.#urlFromForm()), 500);

  /**
   * Removing one active filter, or clearing them all. Both are plain links to
   * the already-correct URL, so this only needs to stop the navigation.
   *
   * @param {Event} event
   * @param {Element} element
   */
  follow = (event, element) => {
    if (!(element instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    this.#apply(new URL(element.href));
  };

  /**
   * @returns {URL} The URL the form's current state describes.
   */
  #urlFromForm() {
    const form = /** @type {HTMLFormElement} */ (this.refs.form);
    const url = new URL(window.location.href);

    // Rebuilt from scratch so unchecking the last box in a group actually
    // drops that group from the query rather than leaving a stale value.
    url.search = '';

    for (const [key, value] of new FormData(form).entries()) {
      if (typeof value === 'string' && value.trim() !== '') url.searchParams.append(key, value);
    }

    return url;
  }

  /**
   * @param {URL} url
   */
  async #apply(url) {
    const sectionId = sectionIdFor(this);
    if (!sectionId) return;

    this.setAttribute('loading', '');
    // pushState, not replaceState: a filtered view is a place the shopper
    // expects the back button to leave.
    window.history.pushState({ facets: true }, '', url);

    try {
      await sectionRenderer.render(sectionId, { url });
      this.#announceCount();
    } catch (error) {
      // Fall back to a real navigation rather than leaving the page showing
      // results that no longer match the URL in the address bar.
      console.warn('[facets]', error);
      window.location.assign(url.toString());
    } finally {
      this.removeAttribute('loading');
    }
  }

  #announceCount() {
    const count = document.querySelector('[data-results-count]')?.getAttribute('data-results-count');
    if (count != null) announce(tCount('results_count', Number(count)));
  }

  #onPopState = () => {
    const sectionId = sectionIdFor(this);
    if (sectionId) sectionRenderer.render(sectionId, { url: new URL(window.location.href) }).catch(() => {});
  };
}

if (!customElements.get('facets-component')) {
  customElements.define('facets-component', FacetsComponent);
}
